import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Mail, Zap, Clock, Send, RefreshCw, Plus, Trash2, ChevronDown, ChevronUp,
  AlertCircle, CheckCircle, TrendingUp, MessageSquare, ListChecks, Bell,
  Loader2, Copy, Check, ExternalLink, Sparkles, RotateCcw, Info,
} from 'lucide-react';
import emailService, {
  EmailMessage, EmailAnalysis, DraftReply, FollowUpSequence, ThreadSummary, PendingContact
} from './emailService';
import apiClient from '@/services/api/apiClient';
import { useToast } from '@/components/common/Toast';
import { PageHeader } from '@/components/common/PageHeader';

// ─── tiny helpers ─────────────────────────────────────────────────────────────
const INTENT_COLORS: Record<string, string> = {
  hot: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400',
  interested: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
  'positive-response': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
  scheduling: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400',
  'needs-info': 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
  objection: 'bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400',
  cold: 'bg-gray-100 text-gray-600 dark:bg-gray-900/40 dark:text-gray-400',
  referral: 'bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-400',
  unsubscribe: 'bg-red-200 text-red-800 dark:bg-red-950/60 dark:text-red-300',
};
const URGENCY_COLORS = (s: number) =>
  s >= 8 ? 'text-red-600 dark:text-red-400' :
  s >= 5 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground';

const SENTIMENT_COLORS: Record<string, string> = {
  positive: 'text-emerald-600 dark:text-emerald-400',
  neutral:  'text-muted-foreground',
  cautious: 'text-amber-600 dark:text-amber-400',
  negative: 'text-red-600 dark:text-red-400',
};

const TIMING_LABELS: Record<string, string> = {
  'immediately':  'Send now',
  'wait-24h':     'Wait 24 hours',
  'wait-48h':     'Wait 48 hours',
  'wait-1week':   'Wait 1 week',
};

function CopyButton({ text, className = '' }: { text: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      className={`flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-border hover:bg-muted transition-colors ${className}`}
    >
      {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <div className={`rounded-xl border border-border bg-card ${className}`}>{children}</div>;
}

function Badge({ label, color }: { label: string; color?: string }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${color || 'bg-muted text-muted-foreground'}`}>
      {label}
    </span>
  );
}

// ─── Tab: Analyse Thread ───────────────────────────────────────────────────────
function AnalyseTab({ contactId, journeyId, stage }: { contactId: string; journeyId?: string; stage?: number }) {
  const { addToast } = useToast();
  const [thread, setThread] = useState<EmailMessage[]>([
    { role: 'sent', body: '' },
    { role: 'received', body: '' },
  ]);
  const [userNote, setUserNote] = useState('');
  const [result, setResult] = useState<{ analysis: EmailAnalysis; model: string } | null>(null);
  const [loading, setLoading] = useState(false);

  const addMessage = (role: 'sent' | 'received') => setThread(t => [...t, { role, body: '' }]);
  const removeMessage = (i: number) => setThread(t => t.filter((_, idx) => idx !== i));
  const updateMessage = (i: number, field: keyof EmailMessage, val: string) =>
    setThread(t => t.map((m, idx) => idx === i ? { ...m, [field]: val } : m));

  const run = async () => {
    if (!thread.some(m => m.body.trim())) return addToast({ type: 'error', title: 'Error', description: 'Add at least one email' });
    setLoading(true);
    try {
      const r = await emailService.analyse({ contactId, journeyId, stage, thread: thread.filter(m => m.body.trim()), userNote });
      setResult(r);
    } catch (e: any) {
      addToast({ type: 'error', title: 'Analysis failed', description: e.response?.data?.error || e.message });
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5">
      {/* Thread builder */}
      <Card className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold flex items-center gap-2"><MessageSquare className="h-4 w-4 text-primary" /> Email Thread</h3>
          <div className="flex gap-2">
            <button onClick={() => addMessage('sent')} className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-border hover:bg-muted transition-colors">
              <Plus className="h-3 w-3" /> Sent
            </button>
            <button onClick={() => addMessage('received')} className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-border hover:bg-muted transition-colors">
              <Plus className="h-3 w-3" /> Received
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {thread.map((msg, i) => (
            <div key={i} className={`rounded-lg border p-3 space-y-2 ${msg.role === 'sent' ? 'border-primary/30 bg-primary/5' : 'border-border bg-muted/30'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <select
                    value={msg.role}
                    onChange={e => updateMessage(i, 'role', e.target.value)}
                    className="text-xs px-2 py-1 rounded border border-border bg-background"
                  >
                    <option value="sent">↑ Sent</option>
                    <option value="received">↓ Received</option>
                  </select>
                  <input
                    value={msg.subject || ''}
                    onChange={e => updateMessage(i, 'subject', e.target.value)}
                    placeholder="Subject (optional)"
                    className="text-xs px-2 py-1 rounded border border-border bg-background flex-1 min-w-32"
                  />
                  <input
                    value={msg.timestamp || ''}
                    onChange={e => updateMessage(i, 'timestamp', e.target.value)}
                    placeholder="Date/time (optional)"
                    className="text-xs px-2 py-1 rounded border border-border bg-background w-36"
                  />
                </div>
                {thread.length > 1 && (
                  <button onClick={() => removeMessage(i)} className="text-muted-foreground hover:text-destructive transition-colors p-1">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <textarea
                value={msg.body}
                onChange={e => updateMessage(i, 'body', e.target.value)}
                placeholder="Email body..."
                rows={3}
                className="w-full text-sm px-3 py-2 rounded-md border border-border bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
          ))}
        </div>

        <textarea
          value={userNote}
          onChange={e => setUserNote(e.target.value)}
          placeholder="Context note for the AI (optional — e.g. 'this contact was referred by their CFO')"
          rows={2}
          className="w-full text-sm px-3 py-2 rounded-md border border-border bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/40"
        />

        <button
          onClick={run}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {loading ? 'Analysing thread…' : 'Analyse Thread'}
        </button>
      </Card>

      {/* Result */}
      {result && (
        <div className="space-y-4">
          {/* Header row */}
          <div className="flex flex-wrap gap-3 items-center">
            <Badge label={result.analysis.intent} color={INTENT_COLORS[result.analysis.intent] || 'bg-muted text-muted-foreground'} />
            <span className={`text-sm font-medium capitalize ${SENTIMENT_COLORS[result.analysis.sentiment.label]}`}>
              {result.analysis.sentiment.label}
            </span>
            <span className="text-xs text-muted-foreground">· {result.analysis.sentiment.reason}</span>
            <div className="ml-auto flex items-center gap-1">
              <span className="text-xs text-muted-foreground">Urgency</span>
              <span className={`text-sm font-bold ${URGENCY_COLORS(result.analysis.urgencyScore)}`}>
                {result.analysis.urgencyScore}/10
              </span>
            </div>
          </div>

          {/* Key insight */}
          <Card className="px-4 py-3 bg-primary/5 border-primary/20">
            <p className="text-sm font-medium text-primary flex items-start gap-2">
              <Sparkles className="h-4 w-4 shrink-0 mt-0.5" />
              {result.analysis.keyInsight}
            </p>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Buying signals */}
            {result.analysis.buyingSignals.length > 0 && (
              <Card className="p-4 space-y-2">
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <TrendingUp className="h-3.5 w-3.5 text-emerald-500" /> Buying Signals
                </h4>
                <ul className="space-y-1.5">
                  {result.analysis.buyingSignals.map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      {s}
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Objections */}
            {result.analysis.objections.length > 0 && (
              <Card className="p-4 space-y-2">
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5 text-orange-500" /> Objections
                </h4>
                <ul className="space-y-1.5">
                  {result.analysis.objections.map((o, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <span className="px-1.5 py-0.5 rounded text-[10px] bg-orange-100 dark:bg-orange-950/40 text-orange-700 dark:text-orange-400 shrink-0">{o.type}</span>
                      {o.objection}
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Questions */}
            {result.analysis.questions.length > 0 && (
              <Card className="p-4 space-y-2">
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <MessageSquare className="h-3.5 w-3.5 text-blue-500" /> Questions to Answer
                </h4>
                <ul className="space-y-1.5">
                  {result.analysis.questions.map((q, i) => (
                    <li key={i} className="text-sm text-foreground">→ {q}</li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Commitments */}
            {result.analysis.commitments.length > 0 && (
              <Card className="p-4 space-y-2">
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <ListChecks className="h-3.5 w-3.5 text-violet-500" /> Commitments
                </h4>
                <ul className="space-y-1.5">
                  {result.analysis.commitments.map((c, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] shrink-0 ${c.who === 'mda' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>{c.who}</span>
                      {c.what}
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>

          {/* Stage advancement + next action */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-4 space-y-2">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Stage Advancement</h4>
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${result.analysis.stageAdvancement.recommended && result.analysis.stageAdvancement.direction === 'forward' ? 'bg-emerald-500' : result.analysis.stageAdvancement.direction === 'backward' ? 'bg-red-500' : 'bg-amber-500'}`} />
                <span className="text-sm font-medium capitalize">{result.analysis.stageAdvancement.direction}</span>
                {result.analysis.stageAdvancement.recommended && <Badge label="Recommended" color="bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400" />}
              </div>
              <p className="text-sm text-muted-foreground">{result.analysis.stageAdvancement.reason}</p>
            </Card>

            <Card className="p-4 space-y-2">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Next Best Action</h4>
              <div className="flex items-center gap-2">
                <Badge label={result.analysis.nextBestAction.action} color="bg-primary/10 text-primary" />
                <span className="text-xs text-muted-foreground">Suggested tone: <span className="font-medium text-foreground">{result.analysis.recommendedReplyTone}</span></span>
              </div>
              <p className="text-sm text-foreground">{result.analysis.nextBestAction.detail}</p>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Draft Reply ──────────────────────────────────────────────────────────
function DraftReplyTab({ contactId, journeyId, stage }: { contactId: string; journeyId?: string; stage?: number }) {
  const { addToast } = useToast();
  const [lastEmail, setLastEmail] = useState('');
  const [tone, setTone] = useState<'warm' | 'direct' | 'formal' | 'concise'>('warm');
  const [maxWords, setMaxWords] = useState(150);
  const [intent, setIntent] = useState('');
  const [userNote, setUserNote] = useState('');
  const [draft, setDraft] = useState<DraftReply | null>(null);
  const [loading, setLoading] = useState(false);
  const [editedBody, setEditedBody] = useState('');

  const run = async () => {
    if (!lastEmail.trim()) return addToast({ type: 'error', title: 'Error', description: 'Paste the email you want to reply to' });
    setLoading(true);
    try {
      const r = await emailService.draftReply({ contactId, journeyId, stage, lastEmail, tone, maxWords, intent: intent || undefined, userNote: userNote || undefined });
      setDraft(r.draft);
      setEditedBody(r.draft.body);
    } catch (e: any) {
      addToast({ type: 'error', title: 'Draft failed', description: e.response?.data?.error || e.message });
    } finally { setLoading(false); }
  };

  const regenerate = () => { setDraft(null); run(); };

  return (
    <div className="space-y-5">
      <Card className="p-5 space-y-4">
        <h3 className="text-sm font-semibold flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /> Reply to this email</h3>

        <textarea
          value={lastEmail}
          onChange={e => setLastEmail(e.target.value)}
          placeholder="Paste the email you received and want to reply to…"
          rows={5}
          className="w-full text-sm px-3 py-2 rounded-md border border-border bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/40"
        />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Tone</label>
            <select value={tone} onChange={e => setTone(e.target.value as any)}
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-background">
              {['warm','direct','formal','concise'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Max words</label>
            <input type="number" value={maxWords} min={50} max={400}
              onChange={e => setMaxWords(Number(e.target.value))}
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-background" />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-muted-foreground block mb-1">Intent to address (optional)</label>
            <input value={intent} onChange={e => setIntent(e.target.value)}
              placeholder="e.g. 'they asked about pricing'"
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-background" />
          </div>
        </div>

        <textarea value={userNote} onChange={e => setUserNote(e.target.value)}
          placeholder="Additional instruction for the AI (optional)"
          rows={2}
          className="w-full text-sm px-3 py-2 rounded-md border border-border bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/40" />

        <button onClick={run} disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {loading ? 'Drafting reply…' : 'Draft Reply'}
        </button>
      </Card>

      {draft && (
        <Card className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-primary">Generated Reply</h3>
            <div className="flex gap-2">
              <button onClick={regenerate} className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-border hover:bg-muted transition-colors">
                <RotateCcw className="h-3 w-3" /> Regenerate
              </button>
              <CopyButton text={`Subject: ${draft.subject}\n\n${editedBody}`} />
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Subject</label>
            <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-muted/30 text-sm font-medium">
              {draft.subject}
              <CopyButton text={draft.subject} className="ml-auto" />
            </div>
          </div>

          {/* Body — editable */}
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Body <span className="text-muted-foreground font-normal">(editable)</span></label>
            <textarea
              value={editedBody}
              onChange={e => setEditedBody(e.target.value)}
              rows={10}
              className="w-full text-sm px-3 py-2.5 rounded-md border border-border bg-background resize-y focus:outline-none focus:ring-2 focus:ring-primary/40 font-mono leading-relaxed"
            />
          </div>

          {/* Meta */}
          <div className="flex flex-wrap gap-3 pt-1 border-t border-border text-xs text-muted-foreground">
            <span>Strategy: <span className="text-foreground font-medium">{draft.reasoning}</span></span>
            <span className="ml-auto flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {TIMING_LABELS[draft.sendTiming] || draft.sendTiming}
            </span>
          </div>

          {/* Alternative opening */}
          {draft.alternativeOpening && (
            <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
              <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-1">Alternative opening</p>
              <p className="text-sm text-foreground">{draft.alternativeOpening}</p>
              <button onClick={() => setEditedBody(b => `${draft.alternativeOpening}\n\n${b.split('\n\n').slice(1).join('\n\n')}`)}
                className="mt-1.5 text-xs text-amber-700 dark:text-amber-400 hover:underline">
                Use this opening instead
              </button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

// ─── Tab: Follow-Up Sequence ───────────────────────────────────────────────────
function FollowUpTab({ contactId, journeyId, stage }: { contactId: string; journeyId?: string; stage?: number }) {
  const { addToast } = useToast();
  const [lastSentEmail, setLastSentEmail] = useState('');
  const [daysSinceSent, setDaysSinceSent] = useState(0);
  const [userNote, setUserNote] = useState('');
  const [result, setResult] = useState<FollowUpSequence | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<number[]>([0]);

  const run = async () => {
    if (!lastSentEmail.trim()) return addToast({ type: 'error', title: 'Error', description: 'Paste the email you sent' });
    setLoading(true);
    try {
      const r = await emailService.followUpSequence({ contactId, journeyId, stage, lastSentEmail, daysSinceSent, userNote: userNote || undefined });
      setResult(r.sequence);
      setExpanded([0]);
    } catch (e: any) {
      addToast({ type: 'error', title: 'Failed', description: e.response?.data?.error || e.message });
    } finally { setLoading(false); }
  };

  const ANGLE_COLORS: Record<string, string> = {
    proof: 'bg-blue-100 text-blue-700', question: 'bg-violet-100 text-violet-700',
    insight: 'bg-amber-100 text-amber-700', 'case-study': 'bg-emerald-100 text-emerald-700',
    reframe: 'bg-orange-100 text-orange-700', breakup: 'bg-red-100 text-red-700',
  };

  return (
    <div className="space-y-5">
      <Card className="p-5 space-y-4">
        <h3 className="text-sm font-semibold flex items-center gap-2"><Send className="h-4 w-4 text-primary" /> Generate Follow-Up Sequence</h3>

        <textarea value={lastSentEmail} onChange={e => setLastSentEmail(e.target.value)}
          placeholder="Paste the email you sent that received no reply…"
          rows={5}
          className="w-full text-sm px-3 py-2 rounded-md border border-border bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/40" />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Days since sent</label>
            <input type="number" value={daysSinceSent} min={0}
              onChange={e => setDaysSinceSent(Number(e.target.value))}
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-background" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Context (optional)</label>
            <input value={userNote} onChange={e => setUserNote(e.target.value)}
              placeholder="e.g. 'they seemed interested on LinkedIn'"
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-background" />
          </div>
        </div>

        <button onClick={run} disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {loading ? 'Building sequence…' : 'Generate 3-Email Sequence'}
        </button>
      </Card>

      {result && (
        <div className="space-y-3">
          {result.sequence.map((email, i) => (
            <Card key={i} className="overflow-hidden">
              <button
                onClick={() => setExpanded(e => e.includes(i) ? e.filter(x => x !== i) : [...e, i])}
                className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/30 transition-colors"
              >
                <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs text-muted-foreground">Day {email.day}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${ANGLE_COLORS[email.angle] || 'bg-muted text-muted-foreground'}`}>
                      {email.angle}
                    </span>
                  </div>
                  <p className="text-sm font-medium truncate">{email.subject}</p>
                </div>
                {expanded.includes(i) ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
              </button>

              {expanded.includes(i) && (
                <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Strategy:</span>
                    <span className="text-xs text-foreground">{email.reasoning}</span>
                  </div>
                  <div className="relative">
                    <pre className="text-sm whitespace-pre-wrap font-sans bg-muted/30 rounded-lg p-3 leading-relaxed">{email.body}</pre>
                    <CopyButton text={`Subject: ${email.subject}\n\n${email.body}`} className="absolute top-2 right-2" />
                  </div>
                </div>
              )}
            </Card>
          ))}

          {/* Abort + phone alternative */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card className="p-3 space-y-1">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Stop sequence if</p>
              <p className="text-sm">{result.abortCondition}</p>
            </Card>
            <Card className="p-3 space-y-1">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">If email fails — try</p>
              <p className="text-sm">{result.phoneAlternative}</p>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Thread Summary ───────────────────────────────────────────────────────
function SummaryTab({ contactId, journeyId, stage }: { contactId: string; journeyId?: string; stage?: number }) {
  const { addToast } = useToast();
  const [thread, setThread] = useState<EmailMessage[]>([{ role: 'sent', body: '' }, { role: 'received', body: '' }]);
  const [userNote, setUserNote] = useState('');
  const [result, setResult] = useState<ThreadSummary | null>(null);
  const [loading, setLoading] = useState(false);

  const addMsg = (role: 'sent' | 'received') => setThread(t => [...t, { role, body: '' }]);
  const removeMsg = (i: number) => setThread(t => t.filter((_, idx) => idx !== i));
  const updateMsg = (i: number, field: keyof EmailMessage, val: string) =>
    setThread(t => t.map((m, idx) => idx === i ? { ...m, [field]: val } : m));

  const run = async () => {
    if (!thread.some(m => m.body.trim())) return addToast({ type: 'error', title: 'Error', description: 'Add at least one email' });
    setLoading(true);
    try {
      const r = await emailService.threadSummary({ contactId, journeyId, stage, thread: thread.filter(m => m.body.trim()), userNote });
      setResult(r.summary);
    } catch (e: any) {
      addToast({ type: 'error', title: 'Failed', description: e.response?.data?.error || e.message });
    } finally { setLoading(false); }
  };

  const HEALTH_COLORS: Record<string, string> = {
    strong: 'text-emerald-600 dark:text-emerald-400',
    warm: 'text-blue-600 dark:text-blue-400',
    neutral: 'text-muted-foreground',
    strained: 'text-red-600 dark:text-red-400',
  };

  return (
    <div className="space-y-5">
      <Card className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold flex items-center gap-2"><ListChecks className="h-4 w-4 text-primary" /> Thread to Summarise</h3>
          <div className="flex gap-2">
            <button onClick={() => addMsg('sent')} className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-border hover:bg-muted">
              <Plus className="h-3 w-3" /> Sent
            </button>
            <button onClick={() => addMsg('received')} className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-border hover:bg-muted">
              <Plus className="h-3 w-3" /> Received
            </button>
          </div>
        </div>

        {thread.map((msg, i) => (
          <div key={i} className={`rounded-lg border p-3 space-y-2 ${msg.role === 'sent' ? 'border-primary/30 bg-primary/5' : 'border-border bg-muted/30'}`}>
            <div className="flex items-center gap-2">
              <select value={msg.role} onChange={e => updateMsg(i, 'role', e.target.value)}
                className="text-xs px-2 py-1 rounded border border-border bg-background">
                <option value="sent">↑ Sent</option>
                <option value="received">↓ Received</option>
              </select>
              {thread.length > 1 && (
                <button onClick={() => removeMsg(i)} className="ml-auto text-muted-foreground hover:text-destructive p-1">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <textarea value={msg.body} onChange={e => updateMsg(i, 'body', e.target.value)}
              placeholder="Email body…" rows={3}
              className="w-full text-sm px-3 py-2 rounded-md border border-border bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/40" />
          </div>
        ))}

        <textarea value={userNote} onChange={e => setUserNote(e.target.value)}
          placeholder="Context for the summary (optional)" rows={2}
          className="w-full text-sm px-3 py-2 rounded-md border border-border bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary/40" />

        <button onClick={run} disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {loading ? 'Summarising…' : 'Generate Handoff Summary'}
        </button>
      </Card>

      {result && (
        <div className="space-y-4">
          {/* Executive summary */}
          <Card className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Executive Summary</h4>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Relationship:</span>
                <span className={`text-sm font-medium capitalize ${HEALTH_COLORS[result.relationshipHealth] || ''}`}>
                  {result.relationshipHealth}
                </span>
              </div>
            </div>
            <p className="text-sm leading-relaxed">{result.executiveSummary}</p>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-4 space-y-2">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                <CheckCircle className="h-3.5 w-3.5 text-emerald-500" /> Key Facts Confirmed
              </h4>
              <ul className="space-y-1">
                {result.keyFacts.map((f, i) => <li key={i} className="text-sm">· {f}</li>)}
              </ul>
            </Card>

            <Card className="p-4 space-y-2">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5 text-amber-500" /> Open Loops
              </h4>
              <ul className="space-y-1">
                {result.openLoops.map((o, i) => <li key={i} className="text-sm">→ {o}</li>)}
              </ul>
            </Card>

            {result.doNotMention.length > 0 && (
              <Card className="p-4 space-y-2">
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5 text-red-500" /> Do Not Mention
                </h4>
                <ul className="space-y-1">
                  {result.doNotMention.map((d, i) => <li key={i} className="text-sm text-red-600 dark:text-red-400">⚠ {d}</li>)}
                </ul>
              </Card>
            )}

            <Card className="p-4 space-y-2">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Suggested Opener</h4>
              <p className="text-sm italic">"{result.suggestedOpener}"</p>
              <CopyButton text={result.suggestedOpener} />
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Pending Contacts (SLA monitor) ─────────────────────────────────────
function PendingTab() {
  const [data, setData] = useState<PendingContact[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await emailService.getPending();
      setData(r.data);
    } catch { setData([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const URGENCY_BG: Record<string, string> = {
    critical: 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800',
    overdue:  'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800',
    ok:       'border-border',
  };
  const URGENCY_DOT: Record<string, string> = {
    critical: 'bg-red-500',
    overdue:  'bg-amber-500',
    ok:       'bg-muted',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Contacts with no email activity past their follow-up SLA.</p>
        <button onClick={load} className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-border hover:bg-muted transition-colors">
          <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
      ) : data.length === 0 ? (
        <Card className="p-8 text-center">
          <CheckCircle className="h-8 w-8 text-emerald-500 mx-auto mb-3" />
          <p className="text-sm font-medium">All contacts within SLA</p>
          <p className="text-xs text-muted-foreground mt-1">No overdue follow-ups right now.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {data.map(c => (
            <Card key={c.contactId} className={`p-4 flex items-center gap-4 ${URGENCY_BG[c.urgency]}`}>
              <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${URGENCY_DOT[c.urgency]}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold">{c.name}</p>
                  <span className="text-xs text-muted-foreground">·</span>
                  <p className="text-xs text-muted-foreground truncate">{c.account}</p>
                </div>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className="text-xs text-muted-foreground">{c.daysSinceActivity}d since last activity</span>
                  <span className="text-xs text-muted-foreground">SLA: {c.slaDays}d</span>
                  <span className="text-xs">Score {c.engagementScore}</span>
                  <span className={`text-xs font-medium ${c.urgency === 'critical' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>
                    {c.urgency === 'critical' ? 'Critical — act now' : 'Overdue'}
                  </span>
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <a href={`/contacts/${c.contactId}`}
                  className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-border hover:bg-muted transition-colors">
                  <ExternalLink className="h-3 w-3" /> View
                </a>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'analyse',   label: 'Analyse Thread',    icon: MessageSquare },
  { id: 'reply',     label: 'Draft Reply',        icon: Mail },
  { id: 'followup',  label: 'Follow-Up Sequence', icon: Send },
  { id: 'summary',   label: 'Thread Summary',     icon: ListChecks },
  { id: 'pending',   label: 'Pending (SLA)',       icon: Bell },
];

export default function EmailAgentPage() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('analyse');
  const [contacts, setContacts] = useState<any[]>([]);
  const [journeys, setJourneys] = useState<any[]>([]);
  const [selectedContact, setSelectedContact] = useState(searchParams.get('contactId') || '');
  const [selectedJourney, setSelectedJourney] = useState(searchParams.get('journeyId') || '');
  const [stage, setStage] = useState<number>(parseInt(searchParams.get('stage') || '0') || 0);

  useEffect(() => {
    apiClient.get('/api/contacts?limit=100').then(r => setContacts(r.data?.data || []));
  }, []);

  useEffect(() => {
    if (selectedContact) {
      apiClient.get(`/api/abm-journeys/contact/${selectedContact}`).then(r => {
        if (r.data?.id) { setJourneys([r.data]); if (!selectedJourney) setSelectedJourney(r.data.id); }
      }).catch(() => setJourneys([]));
    }
  }, [selectedContact]);

  const TabContent = () => {
    const props = { contactId: selectedContact, journeyId: selectedJourney || undefined, stage: stage || undefined };
    if (activeTab === 'analyse')  return <AnalyseTab   {...props} />;
    if (activeTab === 'reply')    return <DraftReplyTab {...props} />;
    if (activeTab === 'followup') return <FollowUpTab   {...props} />;
    if (activeTab === 'summary')  return <SummaryTab    {...props} />;
    if (activeTab === 'pending')  return <PendingTab />;
    return null;
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <PageHeader
        title="Email Agent"
        description="AI-powered email intelligence — analyse threads, draft replies, generate sequences, and monitor follow-up SLAs"
        icon={Mail}
      />

      {/* Contact / Journey selector */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Contact <span className="text-muted-foreground font-normal">(optional — adds context)</span></label>
            <select value={selectedContact} onChange={e => { setSelectedContact(e.target.value); setSelectedJourney(''); }}
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-background">
              <option value="">— No contact selected —</option>
              {contacts.map(c => (
                <option key={c.id} value={c.id}>{c.firstName} {c.lastName} — {c.account?.name || ''}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Journey (auto-loaded)</label>
            <select value={selectedJourney} onChange={e => setSelectedJourney(e.target.value)}
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-background"
              disabled={!journeys.length}>
              <option value="">{journeys.length ? '— Select journey —' : '— No journey found —'}</option>
              {journeys.map(j => <option key={j.id} value={j.id}>Stage {j.currentStage}/14 · {j.completedStages?.length || 0} done</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Current ABM Stage</label>
            <select value={stage} onChange={e => setStage(Number(e.target.value))}
              className="w-full text-sm px-2 py-1.5 rounded-md border border-border bg-background">
              <option value={0}>— Not set —</option>
              {Array.from({ length: 14 }, (_, i) => i + 1).map(n => (
                <option key={n} value={n}>Stage {n}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted/50 rounded-xl p-1 overflow-x-auto">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}>
            <tab.icon className="h-3.5 w-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Active tab content */}
      <TabContent />
    </div>
  );
}
